/**
 * Main tracking function for analytics events.
 */

import type { AnalyticsEvent } from './types/events.js';
import type { EventProperties } from './types/properties.js';
import type { UnvalidatedAnalyticsEvent } from './utils/validation.js';
import { OFF_CONVENTION_DEVICE_PROPERTY_KEYS } from './types/properties.js';
import { getProviders } from './providers/index.js';
import { validateEvent } from './utils/validation.js';
import { enrichWithContext } from './utils/context.js';
import { missingWebFunnelEnvelopeKeys } from './constants/web-funnel.js';

/**
 * Strict mode = dev + test. In strict mode an off-convention device prop is a
 * hard error (fail fast, force migration); in production we never break the user
 * flow, so we drop the offending key and warn. Vite/jest both statically define
 * `process.env.NODE_ENV`, so this resolves to a constant at build time.
 *
 * Per rule: code-comments-required — WHY: drift must scream in dev, stay silent
 * (but recorded) in prod.
 */
const STRICT_DEVICE_PROPS: boolean =
  typeof process === 'undefined' || process.env?.NODE_ENV !== 'production';

/**
 * Thrown by {@link assertCanonicalDeviceProps} in dev/test when a capture call
 * supplies an off-convention device property. Named so tests can assert on it.
 */
export class CanonicalDevicePropertyError extends Error {
  public readonly offendingKeys: readonly string[];

  public constructor(message: string, offendingKeys: readonly string[]) {
    super(message);
    this.name = 'CanonicalDevicePropertyError';
    this.offendingKeys = offendingKeys;
  }
}

/**
 * Runtime enforcement level of the canonical device-prop convention (ENG-3960 §E12).
 *
 * Inspects the caller-supplied properties for forbidden device keys
 * (`device_brand`, `device_model`, `brand`, `model`, `brandId`, `modelId`):
 * - dev/test → throws {@link CanonicalDevicePropertyError}.
 * - prod     → deletes the offending keys, emits a console.warn, returns the
 *              sanitized copy so the event still ships. Never breaks the user flow.
 *
 * Runs on the RAW caller props (before enrichment) so the documented Release-A
 * dual-write — which re-derives legacy aliases downstream — is unaffected.
 *
 * Per rule: ai-code-requires-tests — failure mode (throw) is covered in track.test.ts.
 */
export function assertCanonicalDeviceProps<T extends EventProperties | undefined>(
  properties: T,
  context: { eventName?: string } = {}
): T {
  if (!properties) {
    return properties;
  }

  const offendingKeys = OFF_CONVENTION_DEVICE_PROPERTY_KEYS.filter((key) =>
    Object.prototype.hasOwnProperty.call(properties, key)
  );

  if (offendingKeys.length === 0) {
    return properties;
  }

  const eventSuffix = context.eventName ? ` on event "${context.eventName}"` : '';
  const detail =
    `Off-convention device prop(s) [${offendingKeys.join(', ')}]${eventSuffix}. ` +
    'Use device_model_id / model_name / device_brand_id / brand_name (ENG-3960 §E12).';

  if (STRICT_DEVICE_PROPS) {
    throw new CanonicalDevicePropertyError(detail, offendingKeys);
  }

  // Production: drop the offending keys, keep the event flowing.
  // eslint-disable-next-line no-console -- intentional prod-only drift signal
  console.warn(`Analytics: dropped ${detail}`);
  const sanitized: EventProperties = { ...properties };
  for (const key of offendingKeys) {
    delete (sanitized as Record<string, unknown>)[key];
  }
  return sanitized as T;
}

/**
 * Dev/test-only drift signal for the standalone-web journey funnel (ENG-4330).
 *
 * The four web-funnel events are read as ONE funnel split by `client`, so an
 * envelope key silently going missing does not break the app — it quietly
 * deletes the web half of a dashboard. This warns the developer at the moment
 * the event is emitted. It NEVER throws and NEVER drops the event: the event
 * still ships with whatever envelope it has, and the check is compiled out of
 * production entirely (same NODE_ENV gate as STRICT_DEVICE_PROPS above), so
 * production runtime behavior is byte-identical.
 *
 * Per rule: code-comments-required — WHY: a lost funnel dimension is invisible
 * at runtime; this is the only place it can be made loud without changing what
 * ships.
 */
function warnOnMissingWebFunnelEnvelope(event: AnalyticsEvent): void {
  if (!STRICT_DEVICE_PROPS) {
    return;
  }

  // Spread into a fresh record so the intersection type of EventProperties can
  // be read by key without a type assertion. Dev/test only — the guard above
  // returns first in every shipped build, so production copies nothing.
  const missing = missingWebFunnelEnvelopeKeys(event.name, { ...event.properties });

  if (missing.length === 0) {
    return;
  }

  // eslint-disable-next-line no-console -- intentional dev-only drift signal
  console.warn(
    `Analytics: web-funnel event "${event.name}" is missing envelope key(s) ` +
      `[${missing.join(', ')}]. Client-split funnels (ENG-4330) cannot read it.`
  );
}

/**
 * Track an analytics event.
 *
 * This function:
 * 1. Rejects off-convention device props (canonical convention gate)
 * 2. Validates the event
 * 3. Enriches it with common context
 * 4. Sends it to all configured providers
 *
 * @param event The event to track
 */
export async function track(event: UnvalidatedAnalyticsEvent): Promise<void> {
  // Per ENG-3960 §E12: the canonical device-prop gate runs OUTSIDE the swallowing
  // try/catch below so dev/test drift surfaces loudly (rejected promise) instead
  // of being silently logged. In prod it sanitizes and returns, never throwing.
  // exactOptionalPropertyTypes: event.name is `string | undefined`. Passing
  // `{ eventName: undefined }` is not assignable to `{ eventName?: string }`.
  const guardedProperties = assertCanonicalDeviceProps(
    event.properties,
    event.name !== undefined ? { eventName: event.name } : {}
  );
  // Cast back to AnalyticsEvent: the object spread widens the discriminated union,
  // but `guardedProperties` is the same EventProperties shape `event.properties` had.
  const guardedEvent = { ...event, properties: guardedProperties } as AnalyticsEvent;

  try {
    // Validate the event
    const validatedEvent = validateEvent(guardedEvent);

    // Enrich with common context
    const enrichedEvent = enrichWithContext(validatedEvent);

    // Checked AFTER enrichment — the envelope is what enrichment adds.
    warnOnMissingWebFunnelEnvelope(enrichedEvent);

    // Track with all providers
    const providers = getProviders();
    await Promise.allSettled(providers.map((provider) => provider.track(enrichedEvent)));
  } catch (error) {
    // Log error but don't throw to prevent app disruption
    console.error('Analytics tracking error:', error);
  }
}
